import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ClockIcon,
  DocumentTextIcon,
  CommandLineIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useId,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';

import {
  SEARCH_CATEGORIES,
  type SearchCommandProps,
  type SearchTriggerProps,
  type SearchResultItem,
  type SearchCategory,
} from './SearchCommand.types';

import { useBodyScrollLock } from '@/hooks';

/**
 * Category icons mapping
 */
const CATEGORY_ICONS: Record<SearchCategory['id'], React.ReactNode> = {
  recent: <ClockIcon className="h-4 w-4" />,
  pages: <DocumentTextIcon className="h-4 w-4" />,
  actions: <CommandLineIcon className="h-4 w-4" />,
};

/**
 * Detects if user is on macOS
 */
function useIsMac(): boolean {
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    setIsMac(
      typeof navigator !== 'undefined' &&
        /Mac|iPod|iPhone|iPad/.test(navigator.platform)
    );
  }, []);

  return isMac;
}

/**
 * SearchTrigger - Button to open the command palette
 * Displays the keyboard shortcut hint (Cmd+K or Ctrl+K)
 */
export function SearchTrigger({
  onClick,
  compact = false,
  className = '',
}: SearchTriggerProps) {
  const isMac = useIsMac();
  const modifier = isMac ? '\u2318' : 'Ctrl';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        group relative flex items-center gap-3
        ${compact ? 'p-2.5' : 'px-4 py-2.5'}
        bg-contrast-700/60 hover:bg-contrast-600/80
        border border-stroke-subtle hover:border-stroke-accent
        rounded-xl
        text-neutral-400 hover:text-neutral-200
        transition-all duration-normal
        focus:outline-none focus-visible:ring-state-focus
        backdrop-blur-sm
        ${className}
      `
        .replace(/\s+/g, ' ')
        .trim()}
      aria-label="Open search"
    >
      {/* Subtle sakura glow on hover */}
      <span
        className="
          absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
          bg-gradient-to-r from-primary-400/5 via-transparent to-secondary-400/5
          transition-opacity duration-slow
          pointer-events-none
        "
        aria-hidden="true"
      />

      <MagnifyingGlassIcon className="h-5 w-5 shrink-0 relative z-10" />

      {!compact && (
        <>
          <span className="relative z-10 text-body-md font-sans">
            Search...
          </span>

          <span
            className="
              ml-auto flex items-center gap-1
              text-label-sm font-mono
              px-1.5 py-0.5
              bg-contrast-700 rounded-md
              border border-stroke-subtle
              text-neutral-500 group-hover:text-neutral-400
              relative z-10
              transition-colors duration-fast
            "
          >
            <span>{modifier}</span>
            <span>K</span>
          </span>
        </>
      )}
    </button>
  );
}

/**
 * SearchCommand - Command palette for global search
 *
 * A premium, sakura-themed command palette inspired by Tailwind CSS's search,
 * featuring smooth animations, keyboard navigation, and categorized results.
 *
 * Features:
 * - Blur backdrop with subtle sakura glow
 * - Smooth enter/exit scale animations
 * - Full keyboard navigation (arrows, enter, escape)
 * - Categorized results (Recent, Pages, Actions)
 * - Search highlighting
 * - Focus trap and body scroll lock
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <SearchTrigger onClick={() => setIsOpen(true)} />
 * <SearchCommand
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   results={searchResults}
 *   onSelect={(item) => navigate(item.href)}
 * />
 * ```
 */
export function SearchCommand({
  isOpen,
  onClose,
  results = [],
  placeholder = 'Search pages, actions, and more...',
  onSearch,
  onSelect,
  loading = false,
  className = '',
}: SearchCommandProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const generatedId = useId();
  const modalId = `search-command-${generatedId}`;
  const isMac = useIsMac();

  // Group results by category
  // Note: Results are expected to be pre-filtered by the parent (e.g., via useGlobalSearch)
  const groupedResults = useMemo(() => {
    const groups: Record<SearchCategory['id'], SearchResultItem[]> = {
      recent: [],
      pages: [],
      actions: [],
    };

    results.forEach((item) => {
      if (groups[item.category]) {
        groups[item.category].push(item);
      }
    });

    return groups;
  }, [results]);

  // Flat list for keyboard navigation
  const flatResults = useMemo(
    () =>
      SEARCH_CATEGORIES.flatMap(
        (category) => groupedResults[category.id] || []
      ),
    [groupedResults]
  );

  // Handle open/close with animation
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      setShouldRender(true);
      requestAnimationFrame(() => {
        setIsAnimating(true);
        // Focus input after animation starts
        setTimeout(() => inputRef.current?.focus(), 50);
      });
    } else if (shouldRender) {
      setIsAnimating(false);
      setQuery('');
      setActiveIndex(0);
      // Notify parent to clear search state as well
      onSearch?.('');
      const timer = setTimeout(() => {
        setShouldRender(false);
        previousActiveElement.current?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOpen, shouldRender, onSearch]);

  // Global keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen) {
          // This would need to be handled by parent
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Hold the page's scroll while open — counted with every other overlay's.
  useBodyScrollLock(isOpen);

  // Handle input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      setActiveIndex(0);
      onSearch?.(newQuery);
    },
    [onSearch]
  );

  // Clear search
  const handleClear = useCallback(() => {
    setQuery('');
    setActiveIndex(0);
    onSearch?.('');
    inputRef.current?.focus();
  }, [onSearch]);

  // Handle result selection
  const handleSelect = useCallback(
    (item: SearchResultItem) => {
      item.onSelect?.();
      onSelect?.(item);
      onClose();
    },
    [onSelect, onClose]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev < flatResults.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) =>
            prev > 0 ? prev - 1 : flatResults.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[activeIndex]) {
            handleSelect(flatResults[activeIndex]);
          }
          break;
        case 'Tab':
          // Keep focus within the modal
          e.preventDefault();
          break;
      }
    },
    [flatResults, activeIndex, handleSelect]
  );

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeElement = listRef.current.querySelector(
      '[data-active="true"]'
    ) as HTMLElement;
    if (activeElement) {
      activeElement.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  if (!shouldRender) return null;

  const modal = (
    <div
      className={`
        fixed inset-0 z-50
        flex items-start justify-center
        pt-[15vh] sm:pt-[20vh]
        px-4
        ${isAnimating ? 'bg-scrim-strong backdrop-blur-sm' : 'bg-transparent'}
        transition-all duration-normal
      `.trim()}
      onClick={handleBackdropClick}
      aria-hidden={!isOpen}
    >
      {/* Sakura ambient glow effect */}
      <div
        className={`
          fixed inset-0 pointer-events-none
          ${isAnimating ? 'opacity-100' : 'opacity-0'}
          transition-opacity duration-slow
        `}
        aria-hidden="true"
      >
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-400/8 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-secondary-400/6 rounded-full blur-3xl" />
      </div>

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${modalId}-title`}
        id={modalId}
        onKeyDown={handleKeyDown}
        className={`
          relative w-full max-w-[640px]
          bg-contrast-700/95 backdrop-blur-xl
          border border-stroke-subtle
          rounded-2xl
          shadow-lift-modal
          flex flex-col
          max-h-[70vh]
          overflow-hidden
          ${
            isAnimating
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 -translate-y-4'
          }
          transform transition-all duration-normal ease-out
          ${className}
        `
          .replace(/\s+/g, ' ')
          .trim()}
      >
        {/* Subtle top sakura gradient border */}
        <div
          className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-400/40 to-transparent"
          aria-hidden="true"
        />

        {/* Search Input */}
        <div className="relative flex items-center px-4 py-3 border-b border-stroke-subtle">
          <MagnifyingGlassIcon
            className={`
              h-5 w-5 shrink-0 mr-3
              ${loading ? 'text-primary-400 animate-pulse' : 'text-neutral-500'}
              transition-colors duration-fast
            `}
          />

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="
              flex-1
              bg-transparent
              text-neutral-50 placeholder:text-neutral-500
              text-body-lg font-sans
              focus:outline-none
              caret-primary-400
            "
            aria-label="Search"
            aria-controls={`${modalId}-results`}
            aria-activedescendant={
              flatResults[activeIndex]
                ? `${modalId}-item-${flatResults[activeIndex].id}`
                : undefined
            }
          />

          {query && (
            <button
              type="button"
              onClick={handleClear}
              className="
                p-1 -mr-1 rounded-md
                text-neutral-500 hover:text-neutral-300
                hover:bg-contrast-600/80
                focus:outline-none focus-visible:ring-state-focus
                transition-colors duration-fast
              "
              aria-label="Clear search"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}

          {!query && (
            <span className="text-body-sm text-neutral-600 font-mono">
              {isMac ? 'esc' : 'Esc'} to close
            </span>
          )}
        </div>

        {/* Results */}
        <div
          ref={listRef}
          id={`${modalId}-results`}
          role="listbox"
          className="flex-1 overflow-y-auto overflow-x-hidden py-2 scroll-smooth"
        >
          {results.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-contrast-600/50 mb-4">
                <MagnifyingGlassIcon className="h-6 w-6 text-neutral-500" />
              </div>
              <p className="text-neutral-400 text-body-md">
                {query ? 'No results found' : 'Start typing to search...'}
              </p>
              {query && (
                <p className="text-neutral-600 text-body-sm mt-1">
                  Try a different search term
                </p>
              )}
            </div>
          ) : (
            SEARCH_CATEGORIES.map((category) => {
              const categoryResults = groupedResults[category.id];
              if (!categoryResults || categoryResults.length === 0) return null;

              return (
                <div key={category.id} className="mb-2">
                  {/* Category Header */}
                  <div className="px-4 py-2 flex items-center gap-2">
                    <span className="text-neutral-600">
                      {CATEGORY_ICONS[category.id]}
                    </span>
                    <span className="text-label-sm font-medium text-neutral-500 uppercase tracking-wider">
                      {category.label}
                    </span>
                  </div>

                  {/* Category Items */}
                  {categoryResults.map((item) => {
                    const globalIndex = flatResults.indexOf(item);
                    const isActive = globalIndex === activeIndex;

                    return (
                      <button
                        key={item.id}
                        id={`${modalId}-item-${item.id}`}
                        role="option"
                        aria-selected={isActive}
                        data-active={isActive}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setActiveIndex(globalIndex)}
                        className={`
                          group w-full px-4 py-2.5
                          flex items-center gap-3
                          text-left
                          transition-all duration-fast
                          focus:outline-none
                          ${
                            isActive
                              ? 'bg-gradient-to-r from-primary-400/15 via-primary-400/10 to-transparent'
                              : 'hover:bg-contrast-600/50'
                          }
                        `
                          .replace(/\s+/g, ' ')
                          .trim()}
                      >
                        {/* Item Icon */}
                        {item.icon && (
                          <span
                            className={`
                              shrink-0 w-8 h-8 rounded-lg
                              flex items-center justify-center
                              ${
                                isActive
                                  ? 'bg-sakura-shade text-primary-300'
                                  : 'bg-contrast-600/80 text-neutral-400'
                              }
                              transition-colors duration-fast
                            `}
                          >
                            {item.icon}
                          </span>
                        )}

                        {/* Item Content */}
                        <div className="flex-1 min-w-0">
                          <p
                            className={`
                              text-body-md font-medium truncate
                              ${isActive ? 'text-neutral-50' : 'text-neutral-200'}
                              transition-colors duration-fast
                            `}
                          >
                            {item.title}
                          </p>
                          {item.subtitle && (
                            <p className="text-body-sm text-neutral-500 truncate mt-0.5">
                              {item.subtitle}
                            </p>
                          )}
                        </div>

                        {/* Shortcut or Arrow */}
                        {item.shortcut ? (
                          <div className="flex items-center gap-1">
                            {item.shortcut.map((key, i) => (
                              <span
                                key={i}
                                className="
                                  px-1.5 py-0.5
                                  text-label-sm font-mono
                                  bg-contrast-700 rounded
                                  border border-stroke-subtle
                                  text-neutral-500
                                "
                              >
                                {key}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <ArrowRightIcon
                            className={`
                              h-4 w-4 shrink-0
                              ${isActive ? 'text-primary-400 opacity-100' : 'text-neutral-600 opacity-0 group-hover:opacity-100'}
                              transition-all duration-fast
                            `}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-stroke-subtle bg-contrast-700">
          <div className="flex items-center justify-between text-body-sm text-neutral-600">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-contrast-700 rounded border border-stroke-subtle font-mono">
                  {'\u2191'}
                </kbd>
                <kbd className="px-1.5 py-0.5 bg-contrast-700 rounded border border-stroke-subtle font-mono">
                  {'\u2193'}
                </kbd>
                <span className="text-neutral-500">to navigate</span>
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 bg-contrast-700 rounded border border-stroke-subtle font-mono">
                  {'\u21B5'}
                </kbd>
                <span className="text-neutral-500">to select</span>
              </span>
            </div>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-contrast-700 rounded border border-stroke-subtle font-mono">
                esc
              </kbd>
              <span className="text-neutral-500">to close</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default SearchCommand;
